import {ref, computed} from 'vue'
import {defineStore} from 'pinia'
import {config} from '@/env-config'
import {ValueService} from '@/services'
import {useAuthStore} from './Auth'
import {
    createBlankPayload,
    EntityType,
    type DBGHubPayload,
    type Group,
    type PharmaProgramsProduct,
    type PortfolioValues,
    type Store,
    type StoreSummary,
    type GroupSummary,
    type Dataset
} from '@/data'
import {formatToMonthYear, subtractMonthsAndFormatToMonthYear} from "@/utils.ts";

export enum SelectedEntityType {
    NONE = 'NONE',
    GROUP = 'GROUP',
    PHARMACY = 'PHARMACY'
}

export interface IValuePeriods {
    earnedValueStartMonth: string;
    earnedValueEndMonth: string;
    opportunityMonth: string;
    opportunityStartMonth: string;
    opportunityPeriodsNr: number;
}

export const useValueDataStore = defineStore('valueData', () => {

    const isValueDataStoreLoading = computed((): boolean => {
        return isInitialDataLoading.value || isValueDataLoading.value;
    });

    const isError = computed((): boolean => {
        return isStaticDataLoadError.value || isValueLoadError.value;
    });

    const isInitialDataLoading = ref(false);
    const isValueDataLoading = ref(false);
    const isValueDataLoaded = ref(false);

    // reports if an error occurred during initialising static data
    const isStaticDataLoadError = ref(false);

    // reports if an error occurred solely during value fetching
    const isValueLoadError = ref(false);

    const selectionType = ref<SelectedEntityType>(SelectedEntityType.NONE);
    const selectionId = ref<string | undefined>();
    const valueData = ref<DBGHubPayload>(createBlankPayload());
    const groupList = ref<Group[]>([]);
    const storeList = ref<Store[]>([]);
    const storeSummaryList = ref<StoreSummary[]>([]);
    const groupSummaryList = ref<GroupSummary[]>([]);
    const dataset = ref<Dataset>();
    const valuePeriods = ref<IValuePeriods>();

    const filteredGroupId = ref<string>('');
    const searchValue = ref<string>('');

    const uiRoles = ref<string[]>([]);
    const apiBase = ref<string>(config.apiHost);

    async function handleLogin(): Promise<void> {
        const {isLoggedIn} = useAuthStore();

        if (!isLoggedIn) {
            return;
        }

        if (isInitialDataLoading.value) {
            return;
        }

        try {
            isInitialDataLoading.value = true;
            isStaticDataLoadError.value = false;

            try {
                await fetchEffectiveDataset();
                await initialiseStaticUIRoles();
                await initialiseStaticStoreSummaryList();
                await initialiseStaticGroupSummaryList();
            } catch (e) {
                isStaticDataLoadError.value = true;
            }

            const storedSelection = localStorage.getItem('selectedEntity');

            if (storedSelection) {
                const {type, id} = JSON.parse(storedSelection);
                // Validate the type and id against the current lists
                const isValidGroup =
                    type === EntityType.GROUP && groupSummaryList.value.some((group) => group.groupId === id);
                const isValidStore =
                    type === EntityType.STORE && storeSummaryList.value.some((store) => store.storeId === id);

                if (isValidGroup || isValidStore) {
                    await loadValueData(type, id);
                    return;
                }
            }

            if (groupSummaryList.value.length > 0) {
                await loadValueData(EntityType.GROUP, groupSummaryList.value[0]!.groupId);
            } else if (storeSummaryList.value.length > 0) {
                await loadValueData(EntityType.STORE, storeSummaryList.value[0]!.storeId);
            }

        } finally {
            isInitialDataLoading.value = false;
        }
    }

    async function initialiseStaticGroupSummaryList(): Promise<void> {

        const service = new ValueService();

        const result = await service.refreshGroupSummaryList();
        if (!result) {
            groupSummaryList.value = [];
            throw new Error(`Failed to refresh group summary list: ${JSON.stringify(result)}`);
        } else {
            groupSummaryList.value = result;
        }
    }

    async function initialiseStaticStoreSummaryList(): Promise<void> {

        const service = new ValueService();

        const result = await service.refreshStoreSummaryList();
        if (!result) {
            storeSummaryList.value = [];
            throw new Error(`Failed to refresh store summary list: ${JSON.stringify(result)}`);
        } else {
            storeSummaryList.value = result
        }
    }

    async function loadValueData(type: EntityType, id: string): Promise<void> {

        if (isValueDataLoading.value) {
            return;
        }

        const service = new ValueService();

        isValueLoadError.value = false;
        isValueDataLoading.value = true;

        try {
            const result = await service.loadValueData(type, id);
            if (result) {
                valueData.value = mergeWithBlankData(result, createBlankPayload());
                selectionType.value =
                    type === EntityType.GROUP ? SelectedEntityType.GROUP : SelectedEntityType.PHARMACY;
                selectionId.value = id;

                // Save the selected entity type and ID in local storage
                localStorage.setItem('selectedEntity', JSON.stringify({type, id}));
                isValueDataLoaded.value = true;
            } else {
                isValueLoadError.value = true;
                valueData.value = createBlankPayload();
                selectionType.value = SelectedEntityType.NONE;
                selectionId.value = undefined;
            }
        } finally {
            isValueDataLoading.value = false;
        }
    }

    function handleFilterData(type: 'group', id: string) {
        if (type === 'group') {
            filteredGroupId.value = id;
        }
    }

    function handleClearFilter() {
        filteredGroupId.value = '';
    }

    function getStoresSummaryByGroupId() {
        let filtered = storeSummaryList.value;

        // Filter by groupId if provided
        if (filteredGroupId.value) {
            filtered = filtered.filter((store) => store.groupId === filteredGroupId.value);
        }

        // Filter by searchValue if not blank (case-insensitive)
        if (searchValue.value.trim() !== '') {
            const search = searchValue.value.trim().toLowerCase();
            filtered = filtered.filter((store) => store.storeName.toLowerCase().includes(search));
        }

        return filtered;
    }

    function handleClearSearch() {
        searchValue.value = '';
    }

    async function initialiseStaticUIRoles(): Promise<void> {

        const service = new ValueService();

        const result = await service.fetchRoles();
        if (!result) {
            uiRoles.value = [];
            throw new Error(`Failed to fetch roles for ${JSON.stringify(result)}`);
        } else {
            uiRoles.value = result;
        }
    }

    async function fetchEffectiveDataset(): Promise<Dataset | undefined> {
        if (isValueDataLoading.value) {
            return;
        }

        const service = new ValueService();
        const result = await service.fetchEffectiveDataset();
        if (result) {
            dataset.value = result;
            if (result.periods) {
                valuePeriods.value = {
                    earnedValueStartMonth: formatToMonthYear(result.periods.earnedValueStartDate),
                    earnedValueEndMonth: formatToMonthYear(result.periods.earnedValueEndDate),
                    opportunityPeriodsNr: result.periods.opportunityPeriodsNr || 0,
                    opportunityMonth: formatToMonthYear(result.periods.opportunityDate),
                    opportunityStartMonth: subtractMonthsAndFormatToMonthYear(result.periods.opportunityDate, result.periods.opportunityPeriodsNr-1) // -1 as months are inclusive
                }
            } else {
                valuePeriods.value = {
                    earnedValueStartMonth: "",
                    earnedValueEndMonth: "",
                    opportunityPeriodsNr: 0,
                    opportunityMonth: "",
                    opportunityStartMonth: ""
                }
            }
        }
    }

    return {
        isValueDataStoreLoading,
        selectionType,
        selectionId,
        valueData,
        groupList,
        storeList,
        groupSummaryList,
        storeSummaryList,
        valuePeriods,
        apiBase,
        isError,
        filteredGroupId,
        searchValue,
        isValueDataLoaded,
        uiRoles,
        handleLogin,
        loadValueData,
        getStoresSummaryByGroupId,
        handleFilterData,
        handleClearFilter,
    }
})

// 🔹 Function to merge missing values while keeping existing ones
function deepMergeMissing<T extends object>(target: T, source: T): T {
    if (
        typeof target !== 'object' ||
        target === null ||
        typeof source !== 'object' ||
        source === null
    ) {
        return target
    }

    for (const key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            const sourceValue = source[key] // Store the source value
            const targetValue = target[key] // Store the target value

            if (!(key in target)) {
                // Copy missing properties from source
                if (Array.isArray(sourceValue)) {
                    ;(target as any)[key] = [...sourceValue] // Ensure sourceValue is iterable
                } else {
                    ;(target as any)[key] = sourceValue
                }
            } else if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
                // Handle arrays: Merge missing objects based on unique keys
                mergeArrayObjects(targetValue as any[], sourceValue as any[], 'brandName')
            } else if (
                typeof targetValue === 'object' &&
                targetValue !== null &&
                typeof sourceValue === 'object' &&
                sourceValue !== null &&
                !Array.isArray(sourceValue)
            ) {
                // Recursively merge nested objects
                deepMergeMissing(targetValue as object, sourceValue as object)
            }
        }
    }

    return target
}

// 🔹 Function to merge objects inside arrays based on a unique key (now handling `pharmaprograms`)
function mergeArrayObjects(targetArray: any[], sourceArray: any[], key: string = 'portfolioName') {
    const targetMap = new Map(targetArray.map((item) => [item[key], item]))

    for (const sourceItem of sourceArray) {
        const targetItem = targetMap.get(sourceItem[key])
        if (targetItem) {
            deepMergeMissing(targetItem, sourceItem) // Merge existing item
        } else {
            targetArray.push(sourceItem) // Add missing item
        }
    }
}

// 🔹 Function to ensure missing `pharmaprograms` are included
export function mergeWithBlankData<T extends object>(data: T, blank: T): T {
    const mergedData = deepMergeMissing(data, JSON.parse(JSON.stringify(blank))) // Clone blank to prevent mutation

    // Ensure all expected `pharmaprograms` exist
    if ('pharmaprograms' in blank && 'pharmaprograms' in mergedData) {
        const blankPrograms = (blank as any).pharmaprograms as PharmaProgramsProduct[]
        const mergedPrograms = (mergedData as any).pharmaprograms as PharmaProgramsProduct[]

        const existingBrands = new Set(mergedPrograms.map((p) => p.brandName))

        // Add missing programs
        blankPrograms.forEach((blankProgram) => {
            if (!existingBrands.has(blankProgram.brandName)) {
                mergedPrograms.push(blankProgram)
            }
        })

        mergedPrograms.sort((a, b) => a.brandName.localeCompare(b.brandName))
    }

    // Define manual sort orders
    const manualSortOrders: Record<string, string[]> = {
            arrotex: ['Generics', 'Biologics', 'Specialty Brands'],
            vidacorp: ['Apohealth', "Chemists' Own", 'Health & Beauty']
        }

        // Ensure all expected `portfolioValues` exist for each category
    ;['arrotex', 'vidacorp'].forEach((category) => {
        if (category in blank && category in mergedData) {
            const blankPortfolios = (blank as any)[category].portfolioValues as PortfolioValues[]
            const mergedPortfolios = (mergedData as any)[category].portfolioValues as PortfolioValues[]

            const existingPortfolios = new Set(mergedPortfolios.map((p) => p.portfolioName))

            blankPortfolios.forEach((blankPortfolio) => {
                if (!existingPortfolios.has(blankPortfolio.portfolioName)) {
                    mergedPortfolios.push(blankPortfolio)
                } else {
                    // Merge missing fields within existing portfolio
                    const targetPortfolio = mergedPortfolios.find(
                        (p) => p.portfolioName === blankPortfolio.portfolioName
                    )
                    if (targetPortfolio) {
                        Object.keys(blankPortfolio).forEach((key) => {
                            if (!(key in targetPortfolio)) {
                                ;(targetPortfolio as any)[key] = (blankPortfolio as any)[key]
                            }
                        })
                    }
                }
            })

            // Apply manual sort order
            const sortOrder = manualSortOrders[category]
            mergedPortfolios.sort((a, b) => {
                const indexA = sortOrder!.indexOf(a.portfolioName)
                const indexB = sortOrder!.indexOf(b.portfolioName)

                // If name not in manual sort list, push to the end (index = Infinity)
                return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
            })
        }
    })

    return mergedData
}
